import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Select, Input, Button, Fieldset, ErrorSummary } from 'govuk-react-jsx';
import { useSelector, useDispatch } from 'react-redux';
import {
  getCurrentLanguage,
  getCopyProviderCY,
  getCopyProviderEN,
} from '../../features/app/appSlice';
import {
  getChargepointDetails,
  setFinalNumberOfSockets,
  setSocketAllocationPage,
  setChargepointDetails,
  setIndividualChargepointDetails,
  getDateOfCompletion,
  getGrantTypeSelected,
} from '../../features/installer/installerJourneySlice';
import {
  getApplicationDocumentId,
  getSubcontractorId,
  getSubcontractorCompanyName,
  getSubcontractorFirstName,
  getSubcontractorLastName,
  getSubcontractorEmail,
} from '../../features/application/applicationJourneySlice';
import { checkValidChargepointsNumber } from '../../regex/regex';
import {
  getCpManufacturers,
  getCpModelsByManufacturer,
  saveChargepointModels,
} from '../../firebase';
import useTitle from '../../hooks/useTitle';

const ChargepointsDetails = () => {
  const [errorChargepointsManufacturer, setErrorChargepointManufacturer] =
    useState([]);
  const [errorChargepointsModel, setErrorChargepointModel] = useState([]);
  const [errorChargepointsNumber, setErrorChargepointNumber] = useState([]);
  const [hasError, setHasError] = useState(false);
  const [errorList, setErrorList] = useState([]);
  const errorArray = [];

  const subcontractorId = useSelector(getSubcontractorId);
  const subcontractorCompanyName = useSelector(getSubcontractorCompanyName);
  const subcontractorFirstName = useSelector(getSubcontractorFirstName);
  const subcontractorLastName = useSelector(getSubcontractorLastName);
  const subcontractorEmail = useSelector(getSubcontractorEmail);

  const chargepointDetailsRedux = useSelector(getChargepointDetails);
  const documentId = useSelector(getApplicationDocumentId);

  const currentLanguage = useSelector(getCurrentLanguage);
  const copyProviderCY = useSelector(getCopyProviderCY);
  const copyProviderEN = useSelector(getCopyProviderEN);
  const copyProvider =
    currentLanguage === 'en' ? copyProviderEN : copyProviderCY;
  const dispatch = useDispatch();
  const history = useHistory();
  useTitle(
    'installer.ChargepointsDetails.s15',
    'What chargepoint models did you install? - OZEV Electric Vehicle Chargepoint Grant - GOV.UK'
  );

  const [chargepointManufacturers, setChargepointManufacturers] = useState([]);
  const [chargepointModels, setChargepointModels] = useState([]);

  const dateInstallationsCompletedRedux = useSelector(getDateOfCompletion);
  const grantType = useSelector(getGrantTypeSelected);

  const residential = grantType === 'LANDLORD_RESIDENTIAL_GRANT';

  useEffect(() => {
    getCpManufacturers({ residential }).then((response) => {
      const { data } = response;
      const { manufacturers } = data;
      manufacturers.unshift({
        children: '',
        value: '',
      });

      setChargepointManufacturers(manufacturers);

      if (chargepointModels.length < 1) {
        const manufacturersSet = new Set();

        chargepointDetailsRedux.forEach(({ manufacturer }) => {
          manufacturersSet.add(manufacturer);
        });

        [...manufacturersSet].forEach((manufacturer) => {
          if (manufacturer === '' || manufacturer in chargepointModels) {
            return;
          }

          getCpModelsByManufacturer({
            manufacturer_name: manufacturer,
            get_socket_count: true,
            residential,
          })
            .then((r) => {
              const { data: d } = r;
              const { models } = d;

              setChargepointModels((currentState) => {
                return {
                  ...currentState,
                  [manufacturer]: models,
                };
              });
            })
            .catch(() => {});
        });
      }
    });
  }, []);

  const errorBlankChargepointManufacturer = {
    children: copyProvider.getCopy(
      'installer.ChargepointsDetails.s9',
      'Select a manufacturer'
    ),
  };

  const errorBlankChargepointModel = {
    children: copyProvider.getCopy(
      'installer.ChargepointsDetails.s10',
      'Select a model'
    ),
  };

  const errorBlankNumberChargepoints = {
    children: copyProvider.getCopy(
      'installer.ChargepointsDetails.s11',
      'Enter the number of chargepoints installed'
    ),
  };

  const errorInvalidNumberChargepoints = {
    children: copyProvider.getCopy(
      'installer.ChargepointsDetails.s12',
      'Enter a numeric character greater than 0'
    ),
  };

  const handleChargepointManufacturer = (event, index) => {
    const errorManufacturerCopy = [...errorChargepointsManufacturer];
    const chargepointManufacturer = event.target.value;
    if (event.target.value === '') {
      errorManufacturerCopy[index] = errorBlankChargepointManufacturer;
      setErrorChargepointManufacturer(errorManufacturerCopy);
      setHasError(true);
      errorArray.push({
        ...errorBlankChargepointManufacturer,
        href: '#chargepoint-manufacturer',
      });
    } else {
      errorManufacturerCopy[index] = null;
      setErrorChargepointManufacturer(errorManufacturerCopy);
      errorArray.filter((error) => error.href !== '#chargepoint-manufacturer');
    }
    const chargepointDetailsCopy = [...chargepointDetailsRedux];

    chargepointDetailsCopy[index] = {
      ...chargepointDetailsCopy[index],
      manufacturer: chargepointManufacturer,
      model: '',
      socket: 0,
    };
    dispatch(setChargepointDetails(chargepointDetailsCopy));

    if (chargepointManufacturer.length < 1) {
      return;
    }

    if (chargepointManufacturer in chargepointModels) {
      return;
    }

    getCpModelsByManufacturer({
      manufacturer_name: chargepointManufacturer,
      get_socket_count: true,
      residential,
    }).then((response) => {
      const { data } = response;
      const { models } = data;

      setChargepointModels({
        ...chargepointModels,
        [chargepointManufacturer]: models,
      });
    });
  };

  const handleChargepointsModel = (event, index) => {
    const errorModelCopy = [...errorChargepointsModel];
    if (event.target.value === '') {
      errorModelCopy[index] = errorBlankChargepointModel;
      setErrorChargepointModel(errorModelCopy);
      setHasError(true);
      errorArray.push({
        ...errorBlankChargepointModel,
        href: '#chargepoint-model',
      });
    } else {
      errorModelCopy[index] = null;
      setErrorChargepointModel(errorModelCopy);
      errorArray.filter((error) => error.href !== '#chargepoint-model');
    }
    const numberOfSocketsModel = chargepointModels[
      chargepointDetailsRedux[index].manufacturer
    ].reduce((acc, { name, numberOfSockets }) => {
      if (name === event.target.value) {
        return Number(numberOfSockets);
      }
      return acc;
    }, 0);
    const chargepointDetailsCopy = [...chargepointDetailsRedux];
    chargepointDetailsCopy[index] = {
      ...chargepointDetailsCopy[index],
      model: event.target.value,
      socket: numberOfSocketsModel,
    };
    dispatch(setChargepointDetails(chargepointDetailsCopy));
  };

  const handleNumberChargepoints = (event, index) => {
    const errorChargepointsNumberCopy = [...errorChargepointsNumber];
    const chargepointDetailsCopy = [...chargepointDetailsRedux];
    if (
      event.target.value === '' ||
      event.target.value === '0' ||
      !checkValidChargepointsNumber.test(event.target.value)
    ) {
      errorChargepointsNumberCopy[index] = errorBlankNumberChargepoints;
      setErrorChargepointNumber(errorChargepointsNumberCopy);
      setHasError(true);
      errorArray.push({
        ...errorBlankNumberChargepoints,
        href: '#number-chargepoints',
      });
    } else {
      errorChargepointsNumberCopy[index] = null;
      setErrorChargepointNumber(errorChargepointsNumberCopy);
      errorArray.filter((error) => error.href !== '#number-chargepoints');
    }

    chargepointDetailsCopy[index] = {
      ...chargepointDetailsCopy[index],
      totalInstalled: event.target.value,
    };
    dispatch(setChargepointDetails(chargepointDetailsCopy));
  };

  const handleBtnClick = () => {
    const errorManufacturerCopy = [...errorChargepointsManufacturer];
    const errorModelCopy = [...errorChargepointsModel];
    const errorChargepointsNumberCopy = [...errorChargepointsNumber];

    chargepointDetailsRedux.forEach((chargepoint, index) => {
      const { manufacturer } = chargepoint;
      const { model } = chargepoint;
      const { totalInstalled } = chargepoint;

      if (!(manufacturer in chargepointModels)) {
        setErrorChargepointManufacturer(errorManufacturerCopy);
        setHasError(true);
      }

      // manufacturer
      if (manufacturer === '') {
        errorManufacturerCopy[index] = errorBlankChargepointManufacturer;
        setErrorChargepointManufacturer(errorManufacturerCopy);
        setHasError(true);
        errorArray.push({
          ...errorBlankChargepointManufacturer,
          href: '#chargepoint-manufacturer',
        });
      } else {
        errorManufacturerCopy[index] = null;
        setErrorChargepointManufacturer(errorManufacturerCopy);
        errorArray.filter(
          (error) => error.href !== '#chargepoint-manufacturer'
        );
      }

      // model
      if (model === '') {
        errorModelCopy[index] = errorBlankChargepointModel;
        setErrorChargepointModel(errorModelCopy);
        setHasError(true);
        errorArray.push({
          ...errorBlankChargepointModel,
          href: '#chargepoint-model',
        });
      } else {
        errorModelCopy[index] = null;
        setErrorChargepointModel(errorModelCopy);
        errorArray.filter((error) => error.href !== '#chargepoint-model');
      }

      // chargepoints installed
      if (totalInstalled === '') {
        errorChargepointsNumberCopy[index] = errorBlankNumberChargepoints;
        setErrorChargepointNumber(errorChargepointsNumberCopy);
        setHasError(true);
        errorArray.push({
          ...errorBlankNumberChargepoints,
          href: '#number-chargepoints',
        });
      } else if (totalInstalled === '0') {
        errorChargepointsNumberCopy[index] = errorInvalidNumberChargepoints;
        setErrorChargepointNumber(errorChargepointsNumberCopy);
        setHasError(true);
        errorArray.push({
          ...errorInvalidNumberChargepoints,
          href: '#number-chargepoints',
        });
      } else if (!checkValidChargepointsNumber.test(totalInstalled)) {
        errorChargepointsNumberCopy[index] = errorInvalidNumberChargepoints;
        setErrorChargepointNumber(errorChargepointsNumberCopy);
        setHasError(true);
        errorArray.push({
          ...errorInvalidNumberChargepoints,
          href: '#number-chargepoints',
        });
      } else {
        errorChargepointsNumberCopy[index] = null;
        setErrorChargepointNumber(errorChargepointsNumberCopy);
        errorArray.filter((error) => error.href !== '#number-chargepoints');
      }
    });

    if (!hasError) {
      let subcontractorDetails = null;

      if (subcontractorId) {
        subcontractorDetails = {
          id: subcontractorId,
          companyName: subcontractorCompanyName,
          firstName: subcontractorFirstName,
          lastName: subcontractorLastName,
          email: subcontractorEmail,
        };
      }

      saveChargepointModels({
        documentId,
        installationCompletedDate: dateInstallationsCompletedRedux,
        chargepointModels: chargepointDetailsRedux,
        subcontractorDetails,
      })
        .then((response) => {
          const { data } = response;
          const { chargepointsDetails } = data;

          dispatch(setIndividualChargepointDetails(chargepointsDetails));
          // dispatch(setChargepointDetails(chargepointsDetails));

          let totalSocketsInstalled = 0;

          // eslint-disable-next-line array-callback-return
          chargepointsDetails.map((chargepoint) => {
            totalSocketsInstalled += chargepoint.socket;
          });

          dispatch(setFinalNumberOfSockets(totalSocketsInstalled));
          dispatch(setSocketAllocationPage('chargepointModelDetails'));

          // reset individualChargepointDetails because user might navigatge back to this screen after making chages on summary screen
          // dispatch(setIndividualChargepointDetails([]));
          window.scrollTo(0, 0);

          history.push('/installer/chargepoints-summary');
        })
        .catch(() => {
          setErrorList(errorArray);
          // TODO: Show error
        });
    } else {
      setErrorList(errorArray);
    }
  };

  const handleAddChargePoint = () => {
    const chargepointDetailsCopy = [...chargepointDetailsRedux];
    chargepointDetailsCopy.push({
      manufacturer: '',
      model: '',
      totalInstalled: '',
    });
    dispatch(setChargepointDetails(chargepointDetailsCopy));
  };

  const handleRemove = (event, index) => {
    event.preventDefault();

    const chargepointDetailsCopy = [...chargepointDetailsRedux];
    chargepointDetailsCopy.splice(index, 1);
    dispatch(setChargepointDetails(chargepointDetailsCopy));
  };

  const chargepointModelsToSelectItems = (manufacturer) => {
    if (!(manufacturer in chargepointModels)) {
      return [];
    }

    const items = chargepointModels[manufacturer].map(
      ({ name, numberOfSockets }) => {
        const socketsText =
          numberOfSockets > 1
            ? copyProvider.getCopy(
                'installer.ChargepointsDetails.s13',
                'sockets'
              )
            : copyProvider.getCopy(
                'installer.ChargepointsDetails.s14',
                'socket'
              );
        const nameWithSockets = `${name} (${numberOfSockets} ${socketsText})`;
        return { value: name, children: nameWithSockets };
      }
    );

    items.unshift({
      children: '',
      value: '',
    });

    return items;
  };

  const pageModel = (
    <>
      {errorList.length > 0 && (
        <ErrorSummary
          errorList={errorList}
          titleChildren={copyProvider.getCopy(
            'Common.error-summery',
            'There is a problem'
          )}
        />
      )}
      <h1 className="govuk-heading-xl">
        {copyProvider.getCopy(
          'installer.ChargepointsDetails.s1',
          'What chargepoint models did you install?'
        )}
      </h1>
      <p className="govuk-body">
        {copyProvider.getCopy(
          'installer.ChargepointsDetails.s1a',
          'The grant amount is calculated based on the number of chargepoint sockets. OZEV will pay up to £350 for each socket you installed.'
        )}
      </p>
      {chargepointDetailsRedux.map((chargepoint, index) => {
        const { manufacturer } = chargepoint;
        const { model } = chargepoint;
        const { totalInstalled } = chargepoint;
        const items = chargepointModelsToSelectItems(manufacturer);

        return (
          // TODO: change to a diff id
          // eslint-disable-next-line
          <div key={`${manufacturer}-${index}`}>
            <Fieldset
              legend={{
                children: (
                  <h2>
                    {copyProvider.getCopy(
                      'installer.ChargepointsDetails.s2',
                      'Chargepoint model'
                    )}
                    {` ${index + 1}`}
                  </h2>
                ),
              }}
            >
              <div className="govuk-grid-row">
                {index > 0 && (
                  <Button
                    onClick={(event) => handleRemove(event, index)}
                    className="govuk-button--secondary"
                    name="secondary"
                  >
                    Remove
                  </Button>
                )}
              </div>
              <Select
                errorMessage={errorChargepointsManufacturer[index]}
                onChange={(event) =>
                  handleChargepointManufacturer(event, index)
                }
                className="app-select-width-40"
                id="chargepoint-manufacturer"
                name="chargepoint-manufacturer"
                value={manufacturer}
                label={{
                  children: copyProvider.getCopy(
                    'installer.ChargepointsDetails.s3',
                    'Chargepoint manufacturer'
                  ),
                }}
                items={chargepointManufacturers}
              />
              <Select
                errorMessage={errorChargepointsModel[index]}
                onChange={(event) => handleChargepointsModel(event, index)}
                value={model}
                className="app-select-width-20"
                id="chargepoint-model"
                name="chargepoint-model"
                label={{
                  children: copyProvider.getCopy(
                    'installer.ChargepointsDetails.s4',
                    'Chargepoint model'
                  ),
                }}
                items={items}
              />
              <Input
                errorMessage={errorChargepointsNumber[index]}
                onChange={(event) => handleNumberChargepoints(event, index)}
                value={totalInstalled}
                className="govuk-input--width-4"
                id="number-chargepoints"
                label={{
                  children: copyProvider.getCopy(
                    'installer.ChargepointsDetails.s5',
                    'Number of chargepoints installed for this model'
                  ),
                }}
                name="number-chargepoints"
                type="text"
              />
            </Fieldset>
          </div>
        );
      })}
      {chargepointDetailsRedux.length < 200 && (
        <Button
          onClick={handleAddChargePoint}
          className="govuk-button--secondary"
        >
          {copyProvider.getCopy(
            'installer.ChargepointsDetails.s7',
            'Add another chargepoint model'
          )}
        </Button>
      )}
      <div className="govuk-button-group" id="button-2">
        <Button onClick={handleBtnClick}>
          {copyProvider.getCopy(
            'installer.ChargepointsDetails.s6',
            'Save and continue'
          )}
        </Button>
      </div>
    </>
  );

  return pageModel;
};

export default ChargepointsDetails;
